import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Select from "react-select";
import Breadcrumbs from "../../../../common-elements/breadcrumbs/Breadcrumbs";
import {
  ComposeMessage,
  EnterYourMessages,
  Send,
} from "../../../../utils/Constant";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { dynamicImage } from "../../../../service";

const roleOptions = [
  { value: "sales", label: "Sales" },
  { value: "developers", label: "Developers" },
  { value: "finance", label: "Finance" },
  { value: "admins", label: "Admins" },
  { value: "managers", label: "Managers" },
];

const userData: {
  [key: string]: {
    value: string;
    label: string;
    role: string;
    avatar?: string;
  }[];
} = {
  sales: [
    { value: "all-sales", label: "All (Sales)", role: "sales" },
    {
      value: "john",
      label: "John Doe (Sales)",
      role: "sales",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "jane",
      label: "Jane Smith (Sales)",
      role: "sales",
      avatar: "dashboard-8/user3.png",
    },
  ],
  developers: [
    { value: "all-developers", label: "All (Developers)", role: "developers" },
    {
      value: "alice",
      label: "Alice Johnson (Developers)",
      role: "developers",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "bob",
      label: "Bob Wilson (Developers)",
      role: "developers",
      avatar: "dashboard-8/user3.png",
    },
  ],
  finance: [
    { value: "all-finance", label: "All (Finance)", role: "finance" },
    {
      value: "emma",
      label: "Emma Brown (Finance)",
      role: "finance",
      avatar: "dashboard-8/user3.png",
    },
    {
      value: "mike",
      label: "Mike Davis (Finance)",
      role: "finance",
      avatar: "dashboard-8/user3.png",
    },
  ],
  admins: [
    { value: "all-admins", label: "All (Admins)", role: "admins" },
    {
      value: "sarah",
      label: "Sarah Adams (Admins)",
      role: "admins",
      avatar: "dashboard-8/user3.png",
    },
  ],
  managers: [
    { value: "all-managers", label: "All (Managers)", role: "managers" },
    {
      value: "david",
      label: "David Lee (Managers)",
      role: "managers",
      avatar: "dashboard-8/user3.png",
    },
  ],
};

interface AddNewSMS {
  recipientRoles: string;
  message: string;
}

const ComposeSMSPage: React.FC = () => {
  const [smsValidation, setSMSValidation] = useState(false);
  const [selectedRoles, setSelectedRoles] = useState<typeof roleOptions>([]);
  const [selectedUsers, setSelectedUsers] = useState<any[]>([]);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<AddNewSMS>();

  const onSubmit: SubmitHandler<AddNewSMS> = (data) => {
    if (data.recipientRoles && data.message) {
      console.log("SMS data:", data);
      reset();
      setValue("message", "");
      setSMSValidation(false);
      setSelectedRoles([]);
      setSelectedUsers([]);
      navigate(`${process.env.PUBLIC_URL}/communication/sms`);
    } else {
      setSMSValidation(true);
    }
  };

  const handleRoleChange = (selectedOptions: any) => {
    setSelectedRoles(selectedOptions || []);
    setSelectedUsers([]);
    setValue("recipientRoles", "");
  };

  const handleUserChange = (selectedOptions: any) => {
    let newSelectedUsers = selectedOptions || [];

    const selectedValues = newSelectedUsers.map((opt: any) => opt.value);

    newSelectedUsers = newSelectedUsers.filter((user: any) => {
      if (user.value.startsWith("all-")) {
        return true;
      }
      const roleOfUser = user.role;
      const allOptionForRole = `all-${roleOfUser}`;
      return !selectedValues.includes(allOptionForRole);
    });

    const finalSelection = newSelectedUsers.filter((user: any) => {
      if (!user.value.startsWith("all-")) {
        return true;
      }
      const roleOfAll = user.role;
      return !newSelectedUsers.some(
        (u: any) => u.role === roleOfAll && !u.value.startsWith("all-")
      );
    });

    setSelectedUsers(finalSelection);

    const roles = finalSelection.map((user: any) => {
      if (user.value.startsWith("all-")) {
        return user.role;
      }
      return user.value;
    });
    setValue("recipientRoles", Array.from(new Set(roles)).join(", "));
  };

  const getSelectableUsers = () => {
    if (!selectedRoles.length) {
      return [];
    }

    const userOptions = selectedRoles.flatMap((role) => userData[role.value]);

    userOptions.sort((a, b) => {
      const aIsAll = a.value.startsWith("all-");
      const bIsAll = b.value.startsWith("all-");

      if (aIsAll && !bIsAll) return -1;
      if (!aIsAll && bIsAll) return 1;
      return a.label.localeCompare(b.label);
    });

    const selectedValues = selectedUsers.map((u) => u.value);

    return userOptions.map((user) => {
      const isAllForRoleSelected = selectedValues.includes(`all-${user.role}`);
      const areIndividualUsersInRoleSelected = selectedValues.some(
        (v) =>
          !v.startsWith("all-") &&
          userData[user.role]?.some((u) => u.value === v)
      );

      let isDisabled = false;
      if (user.value.startsWith("all-")) {
        isDisabled = areIndividualUsersInRoleSelected;
      } else {
        isDisabled = isAllForRoleSelected;
      }

      return {
        ...user,
        isDisabled,
      };
    });
  };

  return (
    <div className="page-body">
      <Breadcrumbs
        mainTitle={ComposeMessage}
        parents={[
          {
            label: "Communication",
            link: `${process.env.PUBLIC_URL}/sms/letterbox`,
          },
          {
            label: "SMS",
            link: `${process.env.PUBLIC_URL}/communication/sms`,
          },
        ]}
      />
      <Container fluid>
        <Card>
          <CardBody>
            <Form
              className="needs-validation"
              onSubmit={handleSubmit(onSubmit)}
            >
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">Role:</Label>
                  </Col>
                  <Col sm="10">
                    <Select
                      options={roleOptions}
                      isMulti
                      isClearable
                      value={selectedRoles}
                      onChange={handleRoleChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                    />
                    {smsValidation && !selectedRoles.length && (
                      <div className="invalid-feedback">
                        Please select at least one role
                      </div>
                    )}
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">To:</Label>
                  </Col>
                  <Col sm="10">
                    <Select
                      isMulti
                      isClearable
                      isDisabled={!selectedRoles.length}
                      options={getSelectableUsers()}
                      value={selectedUsers}
                      onChange={handleUserChange}
                      className="basic-multi-select"
                      classNamePrefix="select"
                      formatOptionLabel={(option: any) => (
                        <div className="d-flex align-items-center">
                          {option.avatar && (
                            <img
                              src={dynamicImage(option.avatar)}
                              alt={option.label}
                              style={{
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                marginRight: 8,
                              }}
                            />
                          )}
                          <span>{option.label}</span>
                        </div>
                      )}
                    />
                  </Col>
                </Row>
              </FormGroup>
              <FormGroup>
                <Row>
                  <Col sm="2">
                    <Label className="col-form-label">Message:</Label>
                  </Col>
                  <Col sm="10">
                    <Input
                      type="textarea"
                      rows="4"
                      {...register("message", {
                        required: true,
                        maxLength: 160,
                      })}
                      placeholder={EnterYourMessages}
                      className={`form-control ${
                        smsValidation && errors.message ? "is-invalid" : ""
                      }`}
                    />
                    {smsValidation && errors.message && (
                      <div className="invalid-feedback">
                        {errors.message.type === "required"
                          ? "Message is required"
                          : "Message must be 160 characters or less"}
                      </div>
                    )}
                  </Col>
                </Row>
              </FormGroup>
              <div className="mt-3 d-flex justify-content-end">
                <Button
                  color="secondary"
                  type="button"
                  onClick={() =>
                    navigate(`${process.env.PUBLIC_URL}/communication/sms`)
                  }
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  type="submit"
                  className="ms-2"
                  onClick={() => setSMSValidation(true)}
                >
                  {Send}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default ComposeSMSPage;
